import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Wind, Zap, AlertTriangle, RotateCw, Crown, Camera, Bookmark, BookmarkCheck } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { budgetBandLabel } from "@/lib/budget";
import { useAuth } from "@/context/AuthContext";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";
import FragranceBottleScanner from "@/components/FragranceBottleScanner";

// Fragrance Coach — cologne / perfume recommendations by occasion + season.
// Free users: 1 recommendation/day. Premium: unlimited.

const GENDERS    = ["men", "women", "unisex"];
const OCCASIONS  = ["daily", "date", "luxury", "office", "gym", "evening", "wedding", "casual"];
const SEASONS    = ["spring", "summer", "fall", "winter"];
// Standard 6-band budget shared across Style / Cologne / Grooming / Skincare.
const BUDGETS    = [
  { value: "under-50",  label: "Under $50" },
  { value: "50-plus",   label: "$50+" },
  { value: "100-plus",  label: "$100+" },
  { value: "250-plus",  label: "$250+" },
  { value: "500-plus",  label: "$500+" },
  { value: "luxury",    label: "Luxury · No budget" },
];
const NOTE_TYPES = ["gourmand", "woody", "citrus", "floral", "aquatic", "spicy", "smoky"];

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" className={`chip ${active ? "active" : ""}`} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}

export default function FragranceCoachScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;
  const [gender, setGender] = useState("");
  const [occasion, setOccasion] = useState("");
  const [season, setSeason] = useState("");
  const [budget, setBudget] = useState("100-plus");
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  // Track which picks the user has saved to the wishlist (by index).
  const [savedPicks, setSavedPicks] = useState({});

  const saveToWishlist = async (idx, pick) => {
    setSavedPicks((s) => ({ ...s, [idx]: "saving" }));
    try {
      await api.post("/fragrance/wishlist", {
        name: pick.name,
        notes: pick.notes,
        best_for: pick.best_for,
        budget_band: pick.budget_band || pick.price_band,
        source: "fragrance_coach",
      });
      setSavedPicks((s) => ({ ...s, [idx]: "saved" }));
    } catch {
      setSavedPicks((s) => ({ ...s, [idx]: null }));
    }
  };

  const toggleLike = (v) => setLikes((l) => (l.includes(v) ? l.filter((x) => x !== v) : [...l, v]));

  const generate = async () => {
    if (!occasion) return;
    const payload = { gender: gender || null, occasion, season: season || null, budget_band: budget, likes };
    setError(null);
    setLoading(true);
    setRec(null);
    try {
      const data = await postWithRetry("/fragrance/recommend", payload, { timeout: 60_000, scope: "fragrance" });
      setRec(data);
      try { track("feature_completed", { feature: "fragrance", sub_action: "recommendation" }); } catch { /* ignore */ }
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="screen with-nav fade-in" data-testid="fragrance-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/improve")} aria-label="Back" data-testid="fragrance-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">FRAGRANCE</div>
        {isPremium ? (
          <span className="premium-pill" data-testid="fragrance-quota-pill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid="fragrance-quota-pill">FREE · 1/DAY</span>
        )}
      </div>

      {!rec && !loading && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>FRAGRANCE COACH</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.12 }}>
            The right scent.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>For every moment.</em>
          </h2>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>WHO IS THIS FOR?</div>
            <div style={{ display: "flex", gap: 6 }}>
              {GENDERS.map((g) => (
                <Chip key={g} active={gender === g} onClick={() => setGender(g)} testId={`frag-gender-${g}`}>{g}</Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>OCCASION</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {OCCASIONS.map((o) => (
                <Chip key={o} active={occasion === o} onClick={() => setOccasion(o)} testId={`frag-occasion-${o}`}>{o}</Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>SEASON</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {SEASONS.map((s) => (
                <Chip key={s} active={season === s} onClick={() => setSeason(s)} testId={`frag-season-${s}`}>{s}</Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>BUDGET</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {BUDGETS.map((b) => (
                <Chip key={b.value} active={budget === b.value} onClick={() => setBudget(b.value)} testId={`frag-budget-${b.value}`}>{b.label}</Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>NOTE FAMILIES YOU LIKE</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {NOTE_TYPES.map((n) => (
                <Chip key={n} active={likes.includes(n)} onClick={() => toggleLike(n)} testId={`frag-like-${n}`}>{n}</Chip>
              ))}
            </div>
          </div>

          <button type="button" className="btn btn-gold" onClick={generate} disabled={!occasion} data-testid="fragrance-generate-btn" style={{ marginTop: 14, width: "100%", justifyContent: "center" }}>
            <Zap size={14} /> Find my fragrances
          </button>
        </>
      )}

      {loading && <AnalyzerProgress totalSeconds={12} testId="fragrance-loading" />}
      {error && !loading && (
        <AnalyzerErrorCard error={error} onRetry={generate} onPickNew={() => { setError(null); setRec(null); }} feature="fragrance-recommend" testId="fragrance-error" />
      )}

      {rec && !loading && (
        <>
          <div className="glow-empty-hero" data-testid="fragrance-headline" style={{ padding: "22px 22px" }}>
            <div className="glow-empty-badge"><Wind size={12} strokeWidth={2} /> RECOMMENDED</div>
            <div className="h3" style={{ color: "var(--text)", marginTop: 10 }}>{rec.headline}</div>
            {rec.quota?.remaining === 0 && !rec.quota?.premium && (
              <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 10 }}>
                You&apos;ve used today&apos;s free recommendation. <button type="button" onClick={() => navigate("/premium")} className="gold-text" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>Upgrade for unlimited</button>.
              </div>
            )}
          </div>

          {rec.picks?.length > 0 && (
            <div className="outfit-section" data-testid="fragrance-picks">
              <div className="eyebrow" style={{ marginBottom: 12 }}>YOUR PICKS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {rec.picks.map((p, i) => {
                  const state = savedPicks[i];
                  return (
                    <div key={i} className="card" style={{ padding: 14, position: "relative" }} data-testid={`frag-pick-${i}`}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{p.name}</div>
                        <button
                          type="button"
                          onClick={() => state !== "saved" && saveToWishlist(i, p)}
                          disabled={state === "saving"}
                          className="icon-btn"
                          aria-label={state === "saved" ? "Saved to wishlist" : "Save to wishlist"}
                          data-testid={`frag-pick-wishlist-${i}`}
                          style={{
                            background: state === "saved" ? "var(--gold-soft)" : undefined,
                            borderColor: state === "saved" ? "var(--gold-line)" : undefined,
                            color: state === "saved" ? "var(--gold)" : undefined,
                            flexShrink: 0,
                          }}
                        >
                          {state === "saved" ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                        </button>
                      </div>
                      <div className="tiny" style={{ marginTop: 4, color: "var(--gold)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
                        {budgetBandLabel(p.budget_band || p.price_band)} · {p.longevity} longevity · {p.projection} projection
                      </div>
                      <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 8 }}>{p.vibe}</div>
                      {p.notes && (
                        <div className="tiny" style={{ marginTop: 8, color: "var(--text-faint)" }}>
                          <strong>Top:</strong> {(p.notes.top || []).join(", ")} · <strong>Heart:</strong> {(p.notes.heart || []).join(", ")} · <strong>Base:</strong> {(p.notes.base || []).join(", ")}
                        </div>
                      )}
                      <div className="tiny" style={{ marginTop: 6, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.16em" }}>
                        Best for: {p.best_for}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {rec.watch_outs?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={12} /> WATCH OUTS</div>
              <ul className="bullet-list">{rec.watch_outs.map((w, i) => <li key={i} style={{ color: "var(--text-dim)" }}>{w}</li>)}</ul>
            </div>
          )}

          <button type="button" className="btn btn-outline" onClick={() => setRec(null)} data-testid="fragrance-new-btn" style={{ marginTop: 14, width: "100%", justifyContent: "center" }}>
            <RotateCw size={14} /> Try another occasion
          </button>

          {/* Master spec 2026-02-26: in-store helper — user finds another
              bottle, snaps it, the AI identifies + compares to what we
              just recommended. */}
          <FragranceBottleScanner
            previousRecommendation={rec?.fragrances?.[0]?.name || null}
          />
        </>
      )}
    </div>
  );
}
